import Immutable from 'immutable';
import type { MediaSourceData } from '@lumen5/luminary';

import type { BrandKit, FontAsset, ImageAssetModel } from 'js/src/client';
import { SlideBackgroundModelTypeEnum } from 'js/src/client';
import { getBrandKitById } from 'js/src/data/brand-kits';
import { getFontList } from 'js/src/data/fonts';
import { checkAssetUUID } from 'js/src/data/media';
import StoreORM from 'js/src/data/store-orm';
import { getBrandKitId, setBrandKitId } from 'js/src/data/video';
import { colorSwapperByLuminance } from 'js/src/libs/color-utils';
import type { ImmutableMap } from 'js/src/libs/consts';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    addWatermarkOperation,
    deleteWatermarkOperation,
    removeWatermarkOperation,
    setColorPaletteOperation,
    setTemplateFontsOperation,
} from 'js/src/luminary/luminary-operations';

export type ApplyBrandKitOptions = {
    systemTriggered?: boolean;
    parentActionGroup?: string;
    skipAnalytics?: boolean;
};

/**
 * Finds the indices of watermarks in the template that belong to a specific brand kit.
 */
function findBrandKitWatermarkIndices(
    luminary: { template: { watermarkSources: Array<{ sourceData: { source_id?: string } }> } },
    brandKit: ImmutableMap<BrandKit> | undefined,
): number[] {
    const indices: number[] = [];
    if (!brandKit) return indices;

    const watermarkDetailsArray = brandKit.get('watermarkDetails');
    if (watermarkDetailsArray && Immutable.isList(watermarkDetailsArray)) {
        watermarkDetailsArray.forEach((details: ImmutableMap<ImageAssetModel>) => {
            const sourceId = details.get('source_id');
            if (sourceId) {
                luminary.template.watermarkSources.every((watermark, index) => {
                    if (watermark.sourceData.source_id === sourceId) {
                        indices.push(index);
                        return false;
                    }
                    return true;
                });
            }
        });
    }
    return indices;
}

/**
 * Applies a brand kit to the current video
 *
 * This operation:
 * 1. Applies the brand kit's color palette using luminance-based matching
 * 2. Applies the brand kit's fonts to the template
 * 3. Removes any watermarks from the previously selected brand kit
 * 4. Adds the new brand kit's watermarks
 * 5. Updates the Redux state with the selected brand kit ID
 *
 * @param brandKitId - The ID of the brand kit to apply
 * @param options - Optional configuration for the operation
 */
export const applyBrandKitOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        brandKitId: number,
        options: ApplyBrandKitOptions = {},
    ) => {
        const {
            systemTriggered = false,
            parentActionGroup = uuid(),
            skipAnalytics = false,
        } = options;

        const state = getState();

        // Get the brand kit from Redux
        const brandKit = getBrandKitById(state, brandKitId);
        if (!brandKit) {
            throw new Error(`Brand kit with id ${brandKitId} not found`);
        }

        const brandKitJS = brandKit.toJS();

        // Track analytics
        if (!skipAnalytics) {
            analytics.track('Brand kit selected');
        }

        // 1. Apply colors using luminance-based matching
        const currentColors = luminary.template.colors;
        const brandKitColors = brandKitJS.colors;
        const isDuplicationAllowed = currentColors.length > brandKitColors.length;
        const brandKitClosestMatchesToTemplate = colorSwapperByLuminance(
            currentColors,
            brandKitColors,
            isDuplicationAllowed,
            true, // appendUnusedNewColors
        );
        call(
            setColorPaletteOperation,
            brandKitClosestMatchesToTemplate,
            systemTriggered,
            parentActionGroup,
        );

        // 2. Apply fonts
        const fontList = getFontList(state);
        const fontORM = new StoreORM<FontAsset>(fontList);
        const brandKitFontsUrls: string[] = [];

        brandKitJS.fonts.forEach((brandKitFontId) => {
            const font = fontORM.getByIdOrUndefined(brandKitFontId);
            if (font) {
                brandKitFontsUrls.push(font.get('url'));
            }
        });

        if (brandKitFontsUrls.length > 0) {
            const numFonts = luminary.template.fonts?.length || 0;
            const maxFonts = Math.max(numFonts, brandKitFontsUrls.length);
            const fontsToApply: string[] = [];
            for (let fontIndex = 0; fontIndex < maxFonts; fontIndex++) {
                const fontUrl =
                    brandKitFontsUrls[fontIndex] || brandKitFontsUrls[brandKitFontsUrls.length - 1];
                fontsToApply.push(fontUrl);
            }
            call(setTemplateFontsOperation, fontsToApply, systemTriggered, parentActionGroup);
        }

        // 3. Remove previously selected brand kit watermarks
        const previousBrandKitId = getBrandKitId(state);
        if (previousBrandKitId && previousBrandKitId !== brandKitId) {
            const previousBrandKit = getBrandKitById(state, previousBrandKitId);
            const previousWatermarkIndices = findBrandKitWatermarkIndices(
                luminary,
                previousBrandKit,
            );

            // Remove in reverse order to avoid index shifting issues
            for (let i = previousWatermarkIndices.length - 1; i >= 0; i--) {
                call(deleteWatermarkOperation, previousWatermarkIndices[i], false, true);
            }
        }

        // 4. Add new brand kit watermarks
        if (brandKitJS.watermark && brandKitJS.watermarkDetails) {
            const watermarkDetailsArray = Array.isArray(brandKitJS.watermarkDetails)
                ? brandKitJS.watermarkDetails
                : [brandKitJS.watermarkDetails];

            watermarkDetailsArray.forEach((details) => {
                if (details) {
                    const settings: MediaSourceData = {
                        uuid: checkAssetUUID(details.uuid),
                        type: SlideBackgroundModelTypeEnum.IMAGE,
                        thumbnail_url: details.thumbnail_url,
                        preview_url: details.preview_url,
                        render_url: details.render_url,
                        source: details.source,
                        source_id: details.source_id,
                        license: details.license,
                        is_editorial: details.is_editorial ?? false,
                        width: details.width,
                        height: details.height,
                        title: details.title,
                    };
                    call(addWatermarkOperation, settings, systemTriggered, parentActionGroup);
                }
            });
        } else {
            call(removeWatermarkOperation, systemTriggered, parentActionGroup);
        }

        // 5. Update Redux state with the selected brand kit ID
        dispatch(
            setBrandKitId({
                brandKitId: brandKitJS.id,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
